import { Payer } from "@redotech/congo-enums/payer";
import { Step } from "./return-flow";
import { InputPartType, StepId } from "./return-flow-answers";
import { InputValidation } from "./return-flow-enums";
import {
  AbTestState,
  AddExchangeOrderTagsState,
  AddReturnTagState,
  AddUndeclaredItemState,
  AiReturnReasonsState,
  BlockState,
  CombineLabelState,
  DiscountCodeNameState,
  DonateState,
  DoNothingState,
  DynamicBundleAdjustmentState,
  EvidenceState,
  ExchangeShippingNameState,
  ExchangeShippingRatesState,
  FinalSaleReturnState,
  FlagState,
  InformationState,
  InputState,
  MakeServiceRequestState,
  ManualReviewState,
  MerchantReviewBeforeFilingState,
  MultipleChoiceState,
  OverrideAdjustmentState,
  OverrideGreenReturnState,
  ProcessingActionState,
  RedoManagedLocationState,
  RejectState,
  ReturnProcessingDecisionState,
  ReturnProcessingInformationState,
  ReturnState,
  SendEmailState,
  SendSmsState,
  SeparateLabelsState,
  SetReturnLocationsState,
  SortState,
  SPECIAL_LOCATION_ID,
  StepRef,
  SubmitClaimState,
  SubmitRepairState,
  SubmitWarrantyRegistrationState,
  SubmitWarrantyState,
  TransformProductState,
  TreatmentState,
  TriggerState,
  VerificationState,
  VerifyItemVariantState,
  WaitState,
} from "./return-flow-step-valid";
import { StoreCreditType } from "./return-flow/return-option-enums";

/**
 * Converts a model StepId (integer array index) to a StepRef (string node ID).
 * Pass `String` when the actual string value doesn't matter (e.g. validation).
 */
export type IdFn = (stepId: StepId) => StepRef;

function round(n: number): number {
  return Math.round(n * 100) / 100;
}

export function abTestFromModel(model: Step.ABTest, id: IdFn): AbTestState {
  return {
    customTitle: model.customTitle,
    name: model.name,
    treatments: model.treatments.map(
      (treatment): TreatmentState => ({
        name: treatment.name,
        percentage: treatment.percentage,
        next: id(treatment.next),
      }),
    ),
  };
}

export function addReturnTagFromModel(
  model: Step.AddReturnTag,
  id: IdFn,
): AddReturnTagState {
  return {
    customTitle: model.customTitle,
    tags: model.tags,
    next: model.next ? id(model.next) : undefined,
  };
}

export function addExchangeOrderTagsFromModel(
  model: Step.AddExchangeOrderTags,
  id: IdFn,
): AddExchangeOrderTagsState {
  return {
    customTitle: model.customTitle,
    tags: model.tags,
    copyOriginalOrderTags: model.copyOriginalOrderTags,
    next: model.next ? id(model.next) : undefined,
  };
}

export function aiReturnReasonsFromModel(
  model: Step.AiReturnReasons,
  id: IdFn,
): AiReturnReasonsState {
  const customGuidance = (model.customGuidance ?? []).map((g) => ({
    id: g.id,
    description: g.description,
    next: id(g.next),
  }));

  // Backwards compatibility for nextExchange field getting moved into custom guidance
  // Auto-promote legacy `nextExchange` into a custom guidance entry with the
  // canonical exchange id, unless one already exists.
  if (
    model.nextExchange &&
    !customGuidance.some((g) => g.id === Step.EXCHANGE_GUIDANCE_ID)
  ) {
    customGuidance.push({
      id: Step.EXCHANGE_GUIDANCE_ID,
      description: "Customer wants to exchange",
      next: id(model.nextExchange),
    });
  }

  return {
    stepUUID: model.stepUUID,
    next: id(model.next),
    nextWrongProduct:
      model.nextWrongProduct !== undefined
        ? id(model.nextWrongProduct)
        : undefined,
    nextDamaged:
      model.nextDamaged !== undefined ? id(model.nextDamaged) : undefined,
    nextExchange: undefined,
    customGuidance: customGuidance.length ? customGuidance : undefined,
    additionalAiInfo: model.additionalAiInfo,
    maxQuestions: model.maxQuestions ?? undefined,
    merchantGuidance: model.merchantGuidance,
    customQuestion: model.customQuestion,
    customExplanationSubtext: model.customExplanationSubtext,
  };
}

export function blockFromModel(model: Step.Block): BlockState {
  return {
    stepUUID: model.stepUUID,
    customTitle: model.customTitle,
    message: model.message,
  };
}

export function combineLabelFromModel(
  model: Step.CombineLabel,
): CombineLabelState {
  return { customTitle: model.customTitle };
}

export function discountCodeNameFromModel(
  model: Step.DiscountCodeName,
  id: IdFn,
): DiscountCodeNameState {
  return {
    customTitle: model.customTitle,
    next: model.next ? id(model.next) : undefined,
    prefix: model.prefix || "",
    suffix: model.suffix || "",
  };
}

export function doNothingFromModel(_model: Step.DoNothing): DoNothingState {
  return {};
}

export function dynamicBundleAdjustmentFromModel(
  model: Step.DynamicBundleAdjustment,
  id: IdFn,
): DynamicBundleAdjustmentState {
  return {
    customTitle: model.customTitle,
    next: model.next ? id(model.next) : undefined,
  };
}

export function exchangeShippingNameFromModel(
  model: Step.ExchangeShippingName,
  id: IdFn,
): ExchangeShippingNameState {
  return {
    customTitle: model.customTitle,
    next: model.next ? id(model.next) : undefined,
    shippingName: model.shippingName || "",
  };
}

export function exchangeShippingRatesFromModel(
  model: Step.ExchangeShippingRates,
  id: IdFn,
): ExchangeShippingRatesState {
  return {
    customTitle: model.customTitle,
    next: model.next ? id(model.next) : undefined,
    rates: model.rates.map((rate) => ({
      id: rate.id,
      name: rate.name,
      description: rate.description ?? "",
      price: rate.price,
    })),
  };
}

export function finalSaleReturnFromModel(
  model: Step.FinalSaleReturn,
  id: IdFn,
): FinalSaleReturnState {
  return { next: model.next ? id(model.next) : undefined };
}

export function flagFromModel(model: Step.Flag, id: IdFn): FlagState {
  return {
    customTitle: model.customTitle,
    next: model.next ? id(model.next) : undefined,
    reason: model.reason || "",
  };
}

export function informationFromModel(
  model: Step.Information,
  id: IdFn,
): InformationState {
  return {
    stepUUID: model.stepUUID,
    continueButtonText: model.continueButtonText,
    customTitle: model.customTitle,
    header: model.header,
    message: model.message,
    next: model.next ? id(model.next) : undefined,
  };
}

export function inputFromModel(model: Step.Input, id: IdFn): InputState {
  const settings = model.parts[0]?.textValidationSettings;
  return {
    partUUID: model.parts[0]?.partUUID,
    customTitle: model.customTitle,
    next: id(model.next),
    prompt: model.parts[0]?.message ?? "",
    description: model.parts[0]?.description ?? "",
    validation: model.parts[0]?.validation ?? InputValidation.OPTIONAL,
    type: model.parts[0]?.type || InputPartType.TEXTAREA,
    fieldId: model.parts[0]?.fieldId,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    exampleImage: (model.parts[0]?.exampleImage as URL) || undefined,
    minLength: settings?.minLength,
    maxLength: settings?.maxLength,
    contentType: settings?.contentType,
    customRegex: settings?.customRegex,
    customRegexErrorMessage: settings?.customRegexErrorMessage,
    useAdvancedValidation: settings?.useAdvancedValidation,
    aiVerification: model.parts[0]?.aiVerification,
    aiVerificationStrictness: model.parts[0]?.aiVerificationStrictness,
    extraParts: model.parts.length > 1 ? model.parts.slice(1) : undefined,
  };
}

export function manualReviewFromModel(
  model: Step.ManualReview,
  id: IdFn,
): ManualReviewState {
  return {
    customTitle: model.customTitle,
    next: model.next ? id(model.next) : undefined,
    reason: model.reason || "",
  };
}

export function merchantReviewBeforeFilingFromModel(
  model: Step.MerchantReviewBeforeFiling,
  id: IdFn,
): MerchantReviewBeforeFilingState {
  return {
    customTitle: model.customTitle,
    next: model.next ? id(model.next) : undefined,
  };
}

export function multipleChoiceFromModel(
  model: Step.MultipleChoice,
  id: IdFn,
): MultipleChoiceState {
  return {
    stepUUID: model.stepUUID,
    customTitle: model.customTitle,
    choices: model.options.map((option) => ({
      optionUUID: option.optionUUID,
      name: option.name,
      next: option.next ? id(option.next) : undefined,
      code: option.code,
      additionalAiInfo: option.additionalAiInfo,
    })),
    prompt: model.message,
    description: model.description ?? "",
    reason: model.reason,
    shuffle: model.shuffle,
    additionalAiInfo: model.additionalAiInfo,
  };
}

export function overrideAdjustmentFromModel(
  model: Step.OverrideAdjustment,
  id: IdFn,
): OverrideAdjustmentState {
  return {
    customTitle: model.customTitle,
    next: model.next ? id(model.next) : undefined,
    adjustmentFlat: model.adjustment.flat.toString(),
    adjustmentPercent: (
      Math.round((model.adjustment.proportion - 1) * 10_000) / 100
    ).toString(),
    currencyOverrides: model.adjustment.currencyOverrides
      ? Object.fromEntries(
          Object.entries(model.adjustment.currencyOverrides).map(([k, v]) => [
            k,
            String(v),
          ]),
        )
      : undefined,
    perCurrencyAmountsEnabled: model.adjustment.perCurrencyAmountsEnabled,
    isUseItOrLoseIt: model.isUseItOrLoseIt,
  };
}

export function overrideGreenReturnFromModel(
  model: Step.OverrideGreenReturn,
  id: IdFn,
): OverrideGreenReturnState {
  return {
    next: model.next ? id(model.next) : undefined,
    greenReturnOverride: model.greenReturnOverride,
  };
}

export function rejectFromModel(model: Step.Reject): RejectState {
  return {
    stepUUID: model.stepUUID,
    customTitle: model.customTitle,
    message: model.message,
  };
}

export function returnFromModel(model: Step.Return): ReturnState {
  const exchangeAgentOverrides = buildExchangeAgentOverrides(
    model.exchangeAgentOverrides?.disableExchangeAgent,
    model.exchangeAgentOverrides?.disableVariantRecommendations,
    model.exchangeAgentOverrides?.disableProductRecommendations,
  );
  return {
    customTitle: model.customTitle,
    options: model.options.map((option) => ({
      adjustmentFlat: String(option.adjustment.flat),
      adjustmentPercent: String(
        round((option.adjustment.proportion - 1) * 100),
      ),
      currencyOverrides: option.adjustment.currencyOverrides
        ? Object.fromEntries(
            Object.entries(option.adjustment.currencyOverrides).map(
              ([k, v]) => [k, String(v)],
            ),
          )
        : undefined,
      perCurrencyAmountsEnabled: option.adjustment.perCurrencyAmountsEnabled,
      creditType:
        "creditType" in option.method
          ? option.method.creditType
          : StoreCreditType.GIFT_CARD,
      labelPayer: option.labelPayer,
      type: option.method.type,
      ship: option.ship,
      manual_label: option.manual_label,
      exchangeTypes: option.exchangeTypes,
      chargeCustomerForNewOrderShipping:
        option.chargeCustomerForNewOrderShipping,
      variantExchangeAddOn: option.variantExchangeAddOn,
      variantExchangeAddOnVariantId: option.variantExchangeAddOnVariantId,
      variantExchangeAddOnTitle: option.variantExchangeAddOnTitle,
      variantExchangeAddOnAcceptButton: option.variantExchangeAddOnAcceptButton,
      variantExchangeAddOnRejectButton: option.variantExchangeAddOnRejectButton,
      isUseItOrLoseIt: option.isUseItOrLoseIt,
      overrideAdjustmentWithCode: option.overrideAdjustmentWithCode,
      refundPointsRedeemedForItem: option.refundPointsRedeemedForItem,
      deductEarnedPointsFromRefund: option.deductEarnedPointsFromRefund,
      reimburseForFinalSale: option.reimburseForFinalSale,
      carryOverLineItemProperties: option.carryOverLineItemProperties,
      carryOverLineItemPropertiesOnAddOnAccept:
        option.carryOverLineItemPropertiesOnAddOnAccept,
    })),
    flatShipping: model.flatShipping,
    flatShippingRate: model.flatShippingRate,
    flatShippingCurrencyOverrides: model.flatShippingCurrencyOverrides,
    flatShippingPerCurrencyAmountsEnabled:
      model.flatShippingPerCurrencyAmountsEnabled,
    dropoffFeesEnabled: model.dropoffFeesEnabled ?? !!model.dropoffFees?.length,
    dropoffFees: model.dropoffFees,
    coverPackagePickup: model.coverPackagePickup,
    labelUpcharge: model.labelUpcharge,
    labelUpchargeType: model.labelUpchargeType,
    labelUpchargeValue: model.labelUpchargeValue,
    ...(exchangeAgentOverrides ? { exchangeAgentOverrides } : {}),
  };
}

export function buildExchangeAgentOverrides(
  disableExchangeAgent: boolean | undefined,
  disableVariantRecommendations: boolean | undefined,
  disableProductRecommendations: boolean | undefined,
): Step.ExchangeAgentOverrides | undefined {
  const overrides: Step.ExchangeAgentOverrides = {
    disableExchangeAgent,
    disableVariantRecommendations,
    disableProductRecommendations,
  };

  if (disableExchangeAgent) {
    delete overrides.disableVariantRecommendations;
    delete overrides.disableProductRecommendations;
  } else {
    delete overrides.disableExchangeAgent;
    if (!disableVariantRecommendations) {
      delete overrides.disableVariantRecommendations;
    }
    if (!disableProductRecommendations) {
      delete overrides.disableProductRecommendations;
    }
  }

  if (Object.keys(overrides).length === 0) {
    return undefined;
  }

  return overrides;
}

export function sendEmailFromModel(
  model: Step.SendEmail,
  id: IdFn,
): SendEmailState {
  return {
    customTitle: model.customTitle,
    emailId: model.emailId,
    emailName: model.emailName,
    emailType: model.emailType,
    next: model.next ? id(model.next) : undefined,
  };
}

export function sendSmsFromModel(model: Step.SendSms, id: IdFn): SendSmsState {
  return {
    customTitle: model.customTitle,
    smsId: model.smsId,
    next: model.next ? id(model.next) : undefined,
  };
}

export function separateLabelsFromModel(
  model: Step.SeparateLabels,
): SeparateLabelsState {
  return { customTitle: model.customTitle };
}

export function setReturnLocationsFromModel(
  model: Step.SetReturnLocations,
): SetReturnLocationsState {
  const locations = [...model.locations];
  if (model.useOriginalLocation) {
    locations.push(SPECIAL_LOCATION_ID.ORIGINAL);
  }
  if (model.useOriginalFulfillmentAddress) {
    locations.push(SPECIAL_LOCATION_ID.SHIPMENT_ORIGIN);
  }
  if (model.useRedoManaged) {
    locations.push(SPECIAL_LOCATION_ID.REDO_MANAGED);
  }
  return {
    locations,
    customTitle: model.customTitle,
    restockExternalLocationIds: model.restockExternalLocationIds,
    rateSourceSelection: model.rateSourceSelection,
  };
}

export function donateFromModel(model: Step.SetReturnLocations): DonateState {
  const locations = [...model.locations];
  if (model.useOriginalLocation) {
    locations.push(SPECIAL_LOCATION_ID.ORIGINAL);
  }
  if (model.useOriginalFulfillmentAddress) {
    locations.push(SPECIAL_LOCATION_ID.SHIPMENT_ORIGIN);
  }
  if (model.useRedoManaged) {
    locations.push(SPECIAL_LOCATION_ID.REDO_MANAGED);
  }
  return {
    locations,
    restockExternalLocationIds: model.restockExternalLocationIds,
    customTitle: model.customTitle,
  };
}

export function submitClaimFromModel(
  model: Step.SubmitClaim,
): SubmitClaimState {
  return {
    customTitle: model.customTitle,
    option: {
      method: model.option.method.type,
      creditType:
        "creditType" in model.option.method
          ? model.option.method.creditType
          : StoreCreditType.GIFT_CARD,
      requireSendBack: model.option.requireSendBack,
      chargeCustomerForNewOrderShipping:
        model.option.chargeCustomerForNewOrderShipping || false,
      variantExchangeAddOn: model.option.variantExchangeAddOn || false,
      variantExchangeAddOnVariantId: model.option.variantExchangeAddOnVariantId,
      variantExchangeAddOnTitle: model.option.variantExchangeAddOnTitle,
      variantExchangeAddOnAcceptButton:
        model.option.variantExchangeAddOnAcceptButton,
      variantExchangeAddOnRejectButton:
        model.option.variantExchangeAddOnRejectButton,
      carryOverLineItemProperties: model.option.carryOverLineItemProperties,
      carryOverLineItemPropertiesOnAddOnAccept:
        model.option.carryOverLineItemPropertiesOnAddOnAccept,
    },
  };
}

export function submitRepairFromModel(
  model: Step.SubmitRepair,
): SubmitRepairState {
  return {
    customTitle: model.customTitle,
    option: {
      labelPayer: model.option?.labelPayer ?? Payer.Customer,
      chargeFee: model.option?.chargeFee ?? true,
      repairFee: model.option?.repairFee ?? {
        strategy: Step.RepairFeeMode.Free,
      },
    },
  };
}

export function submitWarrantyRegistrationFromModel(
  model: Step.SubmitWarrantyRegistration,
): SubmitWarrantyRegistrationState {
  return { customTitle: model.customTitle };
}

export function submitWarrantyFromModel(
  model: Step.SubmitWarranty,
): SubmitWarrantyState {
  return {
    customTitle: model.customTitle,
    option: {
      method: model.option.method.type,
      creditType:
        "creditType" in model.option.method
          ? model.option.method.creditType
          : StoreCreditType.GIFT_CARD,
      requireSendBack: model.option.requireSendBack,
      labelPayer: model.option.labelPayer ?? Payer.Merchant,
      adjustmentFlat: model.option.adjustment?.flat.toString(),
      adjustmentPercent: round(
        ((model.option.adjustment?.proportion ?? 1) - 1) * 100,
      ).toString(),
      currencyOverrides: model.option.adjustment?.currencyOverrides
        ? Object.fromEntries(
            Object.entries(model.option.adjustment.currencyOverrides).map(
              ([k, v]) => [k, String(v)],
            ),
          )
        : undefined,
      perCurrencyAmountsEnabled:
        model.option.adjustment?.perCurrencyAmountsEnabled,
      manual_label: model.option.manual_label,
      discountTitle: model.option.discountTitle,
      chargeCustomerForNewOrderShipping:
        model.option.chargeCustomerForNewOrderShipping,
      carryOverLineItemProperties: model.option.carryOverLineItemProperties,
      allowUnpublishedReplacement: model.option.allowUnpublishedReplacement,
    },
  };
}

export function triggerFromModel(model: Step.Trigger, id: IdFn): TriggerState {
  return {
    customTitle: model.customTitle,
    triggerType: model.triggerType,
    next: id(model.next),
  };
}

export function waitFromModel(model: Step.Wait, id: IdFn): WaitState {
  return {
    customTitle: model.customTitle,
    numDays: model.numDays,
    next: id(model.next),
  };
}

// ── RMR (returns-processing) flow converters ─────────────────────────────────
// The persisted model uses numeric `next` / `nextTrueId` / `nextFalseId`
// indices; the editor tracks string StepRefs. `IdFn` does the mapping.
// `null` next is treated as "no edge" (terminal step).

export function sortFromModel(model: Step.Sort, id: IdFn): SortState {
  return {
    customTitle: model.customTitle,
    destination: model.destination,
    next: model.next != null ? id(model.next) : undefined,
    scope: model.scope,
  };
}

export function returnProcessingInformationFromModel(
  model: Step.ReturnProcessingInformation,
  id: IdFn,
): ReturnProcessingInformationState {
  return {
    continueButtonText: model.continueButtonText,
    customTitle: model.customTitle,
    header: model.header,
    imageUrl: model.imageUrl,
    message: model.message,
    next: model.next != null ? id(model.next) : undefined,
    scope: model.scope,
  };
}

export function evidenceFromModel(
  model: Step.Evidence,
  id: IdFn,
): EvidenceState {
  return {
    customTitle: model.customTitle,
    imageUrl: model.imageUrl,
    next: model.next != null ? id(model.next) : undefined,
    notesLabel: model.notesLabel,
    notesPlaceholder: model.notesPlaceholder,
    prompt: model.prompt,
    scope: model.scope,
  };
}

export function addUndeclaredItemFromModel(
  model: Step.AddUndeclaredItem,
  id: IdFn,
): AddUndeclaredItemState {
  return {
    customTitle: model.customTitle,
    next: model.next != null ? id(model.next) : undefined,
    prompt: model.prompt,
    scope: model.scope,
  };
}

export function verificationFromModel(
  model: Step.Verification,
  id: IdFn,
): VerificationState {
  return {
    customTitle: model.customTitle,
    next: model.next != null ? id(model.next) : undefined,
    prompt: model.prompt,
    scope: model.scope,
  };
}

export function verifyItemVariantFromModel(
  model: Step.VerifyItemVariant,
  id: IdFn,
): VerifyItemVariantState {
  return {
    customTitle: model.customTitle,
    matchLabel: model.matchLabel,
    mismatchLabel: model.mismatchLabel,
    next: model.next != null ? id(model.next) : undefined,
    prompt: model.prompt,
    scope: model.scope,
  };
}

export function makeServiceRequestFromModel(
  model: Step.MakeServiceRequest,
  id: IdFn,
): MakeServiceRequestState {
  return {
    customTitle: model.customTitle,
    next: model.next != null ? id(model.next) : undefined,
    photoInstructionsId: model.photoInstructionsId,
    requestKind: model.requestKind,
    scope: model.scope,
  };
}

export function transformProductFromModel(
  model: Step.TransformProduct,
  id: IdFn,
): TransformProductState {
  return {
    customTitle: model.customTitle,
    mode: model.mode,
    next: model.next != null ? id(model.next) : undefined,
    nextMapped: model.nextMapped != null ? id(model.nextMapped) : undefined,
    nextUnmapped:
      model.nextUnmapped != null ? id(model.nextUnmapped) : undefined,
    scope: model.scope,
  };
}

export function returnProcessingDecisionFromModel(
  model: Step.ReturnProcessingDecision,
  id: IdFn,
): ReturnProcessingDecisionState {
  return {
    customTitle: model.customTitle,
    decisionKey: model.decisionKey || undefined,
    falseLabel: model.falseLabel,
    falseLabelColor: model.falseLabelColor,
    imageUrl: model.imageUrl,
    nextFalse: model.nextFalseId != null ? id(model.nextFalseId) : undefined,
    nextTrue: model.nextTrueId != null ? id(model.nextTrueId) : undefined,
    prompt: model.prompt,
    scope: model.scope,
    trueLabel: model.trueLabel,
    trueLabelColor: model.trueLabelColor,
  };
}

export function processingActionFromModel(
  model: Step.ProcessingAction,
  id: IdFn,
): ProcessingActionState {
  return {
    action: model.action,
    customTitle: model.customTitle,
    next: model.next != null ? id(model.next) : undefined,
    scope: model.scope,
  };
}

export function redoManagedLocationFromModel(
  model: Step.RedoManagedLocation,
): RedoManagedLocationState {
  return {
    customTitle: model.customTitle,
    returnsCenterIds: model.returnsCenterIds,
    serviceLevel: model.serviceLevel,
    refulfillmentEnabled: model.refulfillmentEnabled,
    processingFee: model.processingFee,
    consolidationScheduleDays: model.consolidationScheduleDays,
    rateSourceSelection: model.rateSourceSelection,
  };
}
